import Audio1 from '@assets/audio/alarmno.mp3';
import Audio2 from '@assets/audio/call-to-attention.mp3';
import Audio3 from '@assets/audio/clock-alarm.mp3';
import Audio4 from '@assets/audio/emergency-alarm-with-reverb.mp3';
import Audio5 from '@assets/audio/notification.mp3';

export const ALARM_SOUNDS_MAP: Record<string, { label: string; value: string }> = {
  [Audio1]: {
    label: 'Alarma',
    value: Audio1,
  },
  [Audio2]: {
    label: 'Atencion',
    value: Audio2,
  },
  [Audio3]: {
    label: 'Ring',
    value: Audio3,
  },
  [Audio4]: {
    label: 'Emergencia',
    value: Audio4,
  },
  [Audio5]: {
    label: 'Tono Plick',
    value: Audio5,
  },
};

export const DEFAULT_ALARM_SOUND = Audio5; // notification.mp3 — the same tone the slice already fell back to.

// Source file names of the catalog, written out literally instead of derived from the emitted URLs.
//
// Why literal: recovering a legacy preference means matching by file name, and deriving that name
// from a hashed URL needs hash-stripping. Every regex that strips a trailing `-<hash>` also eats the
// hyphens these names really carry: `-[A-Za-z0-9_-]{6,}(?=\.[a-z0-9]+$)` turns
// 'emergency-alarm-with-reverb.mp3' into 'emergency.mp3' and 'call-to-attention.mp3' into
// 'call.mp3' — 3 of the 5 tones would fall to the default instead of being recovered, which is the
// exact opposite of what this bridge is for.
//
// Longest stem first: matching is anchored at the start of the file name, so if a future asset ever
// extends an existing stem ('clock-alarm' vs a hypothetical 'clock-alarm-long'), the more specific
// one has to win.
const LEGACY_SOUND_STEMS: ReadonlyArray<{ stem: string; src: string }> = [
  { stem: 'emergency-alarm-with-reverb', src: Audio4 },
  { stem: 'call-to-attention', src: Audio2 },
  { stem: 'clock-alarm', src: Audio3 },
  { stem: 'notification', src: Audio5 },
  { stem: 'alarmno', src: Audio1 },
];

// `hasOwnProperty`, not `in`: with `in`, 'constructor' and 'toString' would pass as valid sounds.
export const hasAlarmSound = (src: string | null | undefined): boolean =>
  typeof src === 'string' && src.length > 0 && Object.prototype.hasOwnProperty.call(ALARM_SOUNDS_MAP, src);

// `audio.src` reads back as an ABSOLUTE url while the catalog keys are the root-relative urls Vite
// emits, so an exact lookup would answer `false` for every commerce in production. Used only for
// diagnostics (the Sentry canary in safePlay), never to decide what to play.
export const isAlarmSoundUrl = (url: string | null | undefined): boolean =>
  Boolean(url) && Object.keys(ALARM_SOUNDS_MAP).some((src) => url === src || (url as string).endsWith(src));

/**
 * Maps a persisted `selectedSound` onto the asset URL that is actually servable today.
 *
 * The stored value is the asset URL as it looked the day the commerce picked the tone, and those
 * URLs are NOT stable across releases: the Aug-2023 catalog persisted hand-written relative paths
 * ('../assets/audio/alarmno.mp3', commit d0d70371) and today Vite emits content-hashed names. A
 * stale value does not fail loudly, because CloudFront answers any unknown path with the SPA's
 * index.html (200 text/html) instead of a 404: the <audio> element gets an HTML document as its
 * source and rejects play() with NotSupportedError, leaving the order alarm permanently mute
 * (PLICK-FRONT-PANEL-COMERCIO-64: 1434 events / 89 commerces, open since 2023-08-19).
 *
 * Matching by file name recovers the tone the commerce actually chose; falling back to the default
 * only happens when nothing matches, and even that is strictly better than the current silence.
 *
 * @param {string} stored value persisted in `selectedSound`, from any release
 * @returns {string} an asset URL that the current build actually serves
 */
export const resolveAlarmSound = (stored: string | null | undefined): string => {
  if (hasAlarmSound(stored)) return stored as string;
  const fileName = stored?.split(/[?#]/)[0].split('/').pop();
  if (fileName) {
    // `stem.` matches the legacy hand-written path, `stem-` the hashed asset of an older build
    // (those still return audio/mpeg — the deploy workflow uploads without `--delete` — so
    // demoting them to the default would change a tone that still works).
    const match = LEGACY_SOUND_STEMS.find(
      ({ stem }) => fileName.startsWith(`${stem}.`) || fileName.startsWith(`${stem}-`),
    );
    if (match) return match.src;
  }
  return DEFAULT_ALARM_SOUND;
};
